import axios from "axios";
import { toast } from "sonner";
import { getCookie, deleteCookie } from "@/utils/cookies";
import { LOGIN_ROUTE } from "@/utils/const";
import { queryClient } from "@/lib/queryClient";

// Error code 4005 = "Токен доступа не действителен" (invalid token)
const INVALID_TOKEN_ERROR_CODE = 4005;

// Handle token expired errors for all axios requests
const handleAuthError = (error: unknown) => {
  if (
    axios.isAxiosError(error) &&
    error.response?.data?.error === INVALID_TOKEN_ERROR_CODE &&
    getCookie("isAuth") === "true"
  ) {
    // Clear auth data
    deleteCookie("user_token");
    deleteCookie("isAuth");
    localStorage.removeItem("pass_token");

    // Clear React Query cache
    queryClient.clear();

    toast.error("Сессия истекла", {
      description: "Пожалуйста, войдите снова",
    });

    // Redirect to login
    window.location.href = LOGIN_ROUTE;
  }
  return Promise.reject(error);
};

// Global interceptor for direct axios calls
axios.interceptors.response.use((response) => response, handleAuthError);

const $host = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
    "apikey": import.meta.env.VITE_API_KEY,
  },
});

const $authHost = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
    "apikey": import.meta.env.VITE_API_KEY,
  },
});

$authHost.interceptors.request.use((config) => {
  // Проверяем, есть ли токен в заголовках запроса
  const requestToken = config.headers?.usertoken;
  if (requestToken) {
    // Если токен передан в запросе - используем его
    return config;
  } else {
    // Если токена нет в запросе - берем из localStorage
    const userToken = getCookie("user_token");
    if (userToken) {
      config.headers.set("usertoken", userToken);
    }
    return config;
  }
});

// Apply same error handler to $authHost
$authHost.interceptors.response.use((response) => response, handleAuthError);

// NocoBase proxy client
const $nocobaseProxy = axios.create({
  baseURL: import.meta.env.VITE_NOCOBASE_PROXY_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

$nocobaseProxy.interceptors.request.use((config) => {
  const userToken = getCookie("user_token");
  if (userToken) {
    config.headers.set("X-User-Token", userToken);
  }
  // The login phone tells the proxy which NocoBase account to use. The proxy
  // verifies it against the phones returned by 1C for this user_token, so it
  // can't be spoofed — see workers/nocobase-proxy/src/index.ts handleProxy.
  const loginPhone = getCookie("phone");
  if (loginPhone) {
    config.headers.set("X-Phone-Number", loginPhone);
  }
  return config;
});

// Direct worker client (without /proxy prefix) for non-NocoBase endpoints
const workerBaseUrl = (import.meta.env.VITE_NOCOBASE_PROXY_URL ?? "").replace(/\/proxy\/?$/, "");

const $worker = axios.create({
  baseURL: workerBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

export { $host, $authHost, $nocobaseProxy, $worker };
